import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Products2Hero.css';

const items = [
  'Certified Refurbished',
  'Intel & AMD Processors',
  '12-Month Warranty',
  '70% Below MSRP',
  'NVMe SSD Storage',
  'Premium Magnesium Chassis',
  '50-Point Quality Audit',
  'Eco-Friendly Tech',
  'Ships in 48 Hours',
];

const MarqueeStrip = ({ items: marqueeItems }) => {
  const doubled = [...marqueeItems, ...marqueeItems];

  return (
    <div className="marquee-container w-full overflow-hidden border-y border-white/10 bg-evergreen/90 py-5 relative z-20 backdrop-blur-xl">
      <div className="marquee-track">
        {doubled.map((item, i) => (
          <span key={i} className="inline-flex items-center gap-8 px-8">
            <span className="text-[12px] font-bold uppercase tracking-[0.25em] text-white">
              {item}
            </span>
            <span className="w-1 h-1 rounded-full bg-gold/60 flex-shrink-0" />
          </span>
        ))}
      </div>
    </div>
  );
};

const GradientBars = () => {
  const numBars = 15;

  const calculateHeight = (index, total) => {
    const position = index / (total - 1);
    const maxHeight = 100;
    const minHeight = 30;
    
    const center = 0.5;
    const distanceFromCenter = Math.abs(position - center);
    const heightPercentage = Math.pow(distanceFromCenter * 2, 1.2);
    
    return minHeight + (maxHeight - minHeight) * heightPercentage;
  };

  return (
    <div className="gradient-bar-container">
      <div 
        className="flex h-full w-full"
        style={{
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        {Array.from({ length: numBars }).map((_, index) => {
          const height = calculateHeight(index, numBars);
          return (
            <div
              key={index}
              className="gradient-bar"
              style={{
                transform: `scaleY(${height / 100})`,
                animationDelay: `${index * 0.1}s`,
                outline: '1px solid rgba(0, 0, 0, 0)',
                boxSizing: 'border-box',
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

const Products2Hero = ({
  tagline = "Premium Performance · Certified Refurbished · 12-Month Warranty",
  title1 = "Performance",
  title2 = "That Redefines",
  title3 = "Everything.",
  description = "Discover professional-grade laptops at up to 70% off. Meticulously audited and backed by our comprehensive guarantee.",
  button1Text = "Shop Laptops",
  button1Link = "#laptops-collection",
  button2Text = "View Cart",
  button2Link = "/cart",
  button2OnClick,
  marqueeItems = items,
  modern = false,
  className = ""
}) => {
  const headlineRef = useRef(null);

  useEffect(() => {
    const el = headlineRef.current;
    if (!el) return;
    
    // Smooth entry animation
    el.style.opacity = '1';
    el.style.transform = 'translateY(0)';

    // Subtle parallax effect on mouse move
    const handleMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 12;
      const y = (e.clientY / window.innerHeight - 0.5) * 8;
      el.style.transform = `translate(${x}px, ${y}px)`;
    };
    
    window.addEventListener('mousemove', handleMove);
    return () => window.removeEventListener('mousemove', handleMove);
  }, []);

  return (
    <header id="products2hero" className={`relative w-full min-h-[70vh] md:min-h-screen overflow-hidden bg-evergreen font-system mb-24 ${modern ? 'hero-modern' : ''} ${className}`}>
      <GradientBars />
      
      {/* Overlay elements for depth */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-30">
        <div className="absolute inset-0 grid-dot" />
        <div className="absolute inset-0 bg-gradient-to-b from-evergreen/40 via-transparent to-evergreen/80" />
      </div>

      {/* Content wrapper */}
      <div className={`relative z-10 min-h-[60vh] md:min-h-[80vh] flex flex-col justify-start md:justify-center pb-10 pt-48 md:pt-72 px-6 md:px-16 lg:px-24 max-w-screen-2xl mx-auto`}>
        <div 
          ref={headlineRef} 
          className="transition-all duration-1000 ease-out opacity-0 translate-y-8 mb-12"
        >
          <p className="text-[11px] font-semibold uppercase tracking-ultra text-wisteria mb-8">
            {tagline}
          </p>

          <h1 className="font-fraunces text-[clamp(2.5rem,7vw,7.5rem)] leading-[0.9] tracking-tight text-white">
            <span className="block">{title1}</span>
            {title2 && <span className="block italic text-white/90">{title2}</span>}
            {title3 && <span className="block italic" style={{ color: 'var(--gold)' }}>{title3}</span>}
          </h1>

          <p className="mt-6 text-white/60 text-lg max-w-lg leading-relaxed font-light">
            {description}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-start gap-6">
            <a
              href={button1Link}
              className="inline-flex items-center gap-3 bg-gold text-white text-[12px] font-bold uppercase tracking-ultra px-10 py-4 hover:bg-gold-light transition-colors duration-300 gold-shimmer rounded-sm scroll-smooth"
            >
              {button1Text}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
            <Link
              to={button2Link}
              onClick={button2OnClick}
              className="inline-flex items-center gap-3 border border-white/30 text-white/80 text-[12px] font-medium uppercase tracking-ultra px-10 py-4 hover:border-wisteria hover:text-wisteria transition-colors duration-300 rounded-sm"
            >
              {button2Text}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4H6z" />
                <path d="M3 6h18" />
                <path d="M16 10a4 4 0 01-8 0" />
              </svg>
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-20 md:bottom-22 right-8 z-10 flex flex-col items-center gap-3 pointer-events-none">
        <span className="text-[9px] uppercase tracking-ultra text-white/30 rotate-90 origin-center">Scroll</span>
        <div className="w-px h-16 bg-gradient-to-b from-white/30 to-transparent" />
      </div>

      <MarqueeStrip items={marqueeItems} />
    </header>
  );
};

export default Products2Hero;
